/**
 * CoverageTable.tsx
 *
 * Searchable table showing per-jurisdiction searchable artifact coverage.
 * Query matches the user-provided SQL with per-jurisdiction breakdown.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Loader2, AlertTriangle, Search } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { JurisdictionSourcesModal } from './JurisdictionSourcesModal';

interface CoverageRow {
  jurisdiction: string;
  jurisdiction_id: string | null;
  total: number;
  searchable: number;
  searchable_pct: number;
}

interface CoverageData {
  summary: {
    total_artifacts: number;
    text_extracted: number;
    chunked: number;
    searchable: number;
    searchable_pct: number;
  };
  rows: CoverageRow[];
}

export const CoverageTable: React.FC<{ refreshKey?: number }> = ({
  refreshKey = 0,
}) => {
  const [data, setData] = useState<CoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedJurName, setSelectedJurName] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleRowClick = useCallback((name: string) => {
    setSelectedJurName(name);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await apiFetch('/api/source-health/coverage');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json.data as CoverageData);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    if (!search.trim()) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter((r) => r.jurisdiction.toLowerCase().includes(q));
  }, [data, search]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 bg-red-50 border border-red-300 text-red-800 rounded-xl p-4 text-sm">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 text-red-600" />
        {error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Summary row */}
      {data.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard
            label="Total Artifacts"
            value={data.summary.total_artifacts.toLocaleString()}
          />
          <StatCard
            label="Text Extracted"
            value={`${data.summary.total_artifacts > 0 ? ((data.summary.text_extracted / data.summary.total_artifacts) * 100).toFixed(2) : '0.00'}%`}
            sub={`${data.summary.text_extracted.toLocaleString()} of ${data.summary.total_artifacts.toLocaleString()}`}
          />
          <StatCard
            label="Chunked"
            value={data.summary.chunked.toLocaleString()}
          />
          <StatCard
            label="Searchable"
            value={data.summary.searchable.toLocaleString()}
            highlight
          />
          <StatCard
            label="Coverage"
            value={`${Number(data.summary.searchable_pct ?? 0).toFixed(2)}%`}
            highlight
          />
        </div>
      )}

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search jurisdictions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      </div>

      {/* Table */}
      <div
        className="bg-white rounded-lg border border-gray-200 shadow-sm"
        style={{ maxHeight: 'calc(8 * 2.75rem + 2.5rem)', overflowY: 'auto' }}
      >
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr className="border-b border-gray-200">
              <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700">
                Jurisdiction
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-bold text-gray-700">
                Total
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-bold text-gray-700">
                Searchable
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-bold text-gray-700">
                %
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-gray-500 py-4">
                  {search
                    ? `No jurisdictions match "${search}"`
                    : 'No data available'}
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.jurisdiction}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(r.jurisdiction)}
                  title="Click to view source breakdown"
                >
                  <td className="px-4 py-2 text-sm font-medium text-gray-900 underline decoration-dotted underline-offset-2">
                    {r.jurisdiction}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600 text-right">
                    {r.total.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600 text-right">
                    {r.searchable.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    <span
                      className={`font-semibold ${
                        r.searchable_pct > 90
                          ? 'text-green-600'
                          : r.searchable_pct >= 50
                            ? 'text-amber-600'
                            : 'text-red-600'
                      }`}
                    >
                      {Number(r.searchable_pct ?? 0).toFixed(2)}%
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <JurisdictionSourcesModal
        jurisdictionName={selectedJurName ?? ''}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

function StatCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${
        highlight ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
      }`}
    >
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={`text-xl font-bold ${
          highlight ? 'text-blue-700' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}
